// client/src/components/ProductQA.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Box, Typography, TextField, Button, Stack, Divider, Alert } from '@mui/material';
import { AuthContext } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/media';

const ProductQA = ({ productId, sellerId }) => {
  const { user, token } = useContext(AuthContext);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [answerDrafts, setAnswerDrafts] = useState({});

  const fetchQuestions = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/products/${productId}/questions`);
      if (response.ok) setQuestions(await response.json());
    } catch {
      // Non-critical section — fail silently rather than blocking the rest of the page.
    }
  }, [productId]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const canAnswer = user && (user.role === 'admin' || user.userId === sellerId);

  const handleAsk = async (e) => {
    e.preventDefault();
    if (!newQuestion.trim()) return;
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/products/${productId}/questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question: newQuestion.trim() }),
      });
      if (!response.ok) throw new Error();
      setNewQuestion('');
      fetchQuestions();
    } catch {
      alert('Failed to submit your question.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnswer = async (questionId) => {
    const answer = (answerDrafts[questionId] || '').trim();
    if (!answer) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/questions/${questionId}/answer`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ answer }),
      });
      if (!response.ok) throw new Error();
      setAnswerDrafts((prev) => ({ ...prev, [questionId]: '' }));
      fetchQuestions();
    } catch {
      alert('Failed to submit your answer.');
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>Questions & Answers</Typography>

      {user ? (
        <Box component="form" onSubmit={handleAsk} sx={{ mb: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small" fullWidth placeholder="Ask a question about this product..."
              value={newQuestion} onChange={(e) => setNewQuestion(e.target.value)}
            />
            <Button type="submit" variant="contained" disabled={submitting || !newQuestion.trim()}>Ask</Button>
          </Stack>
        </Box>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>Sign in to ask a question.</Alert>
      )}

      {questions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No questions yet — be the first to ask.</Typography>
      ) : (
        <Stack spacing={2} divider={<Divider />}>
          {questions.map((q) => (
            <Box key={q.id}>
              <Typography variant="body2" fontWeight={600}>Q: {q.question}</Typography>
              <Typography variant="caption" color="text.disabled">Asked by {q.asker_email}</Typography>
              {q.answer ? (
                <Typography variant="body2" sx={{ mt: 1, pl: 2, borderLeft: 3, borderColor: 'primary.main' }}>
                  A: {q.answer}
                </Typography>
              ) : canAnswer ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                  <TextField
                    size="small" fullWidth placeholder="Write an answer..."
                    value={answerDrafts[q.id] || ''}
                    onChange={(e) => setAnswerDrafts((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  />
                  <Button size="small" variant="outlined" onClick={() => handleAnswer(q.id)} disabled={!(answerDrafts[q.id] || '').trim()}>
                    Answer
                  </Button>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Awaiting an answer from the seller.</Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ProductQA;
